import React, { useEffect, useRef, useState } from "react";
import { useToastHandler } from "../../common/ToastHandler";
import useApi from "../../../utils/api-manager/Helper/useApi";
import { ToastContainer } from "react-toastify";

const S = {
    root: {
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
        color: "#1f2937",
        padding: "32px 16px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },

    card: {
        width: "100%",
        maxWidth: 720,
        background: "#ffffff",
        borderRadius: 18,
        boxShadow: "0 6px 25px rgba(0,0,0,0.08)",
        overflow: "hidden",
        border: "1px solid #e5e7eb",
    },


    title: {
        fontSize: 22,
        fontWeight: 800,
        color: "#111827",
    },
    subtitle: {
        fontSize: 13,
        color: "#6b7280",
    },


    tabBar: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        background: "#f9fafb",
        borderBottom: "1px solid #e5e7eb",
    },

    tab: (active, color) => ({
        padding: "14px 0",
        background: "#fff",
        border: "none",
        borderBottom: active
            ? `3px solid ${color}`
            : "3px solid transparent",
        color: active ? color : "#6b7280",
        fontWeight: 700,
        fontSize: 14,
        cursor: "pointer",
    }),

    body: {
        padding: "24px",
    },


    banner: (bg, border, text) => ({
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 10,
        padding: "12px",
        marginBottom: 16,
        color: text,
        fontWeight: 800,
        fontSize: 14,
        textAlign: "center",
    }),


    instructionBox: {
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "12px 16px",
        marginBottom: 16,
        display: "flex",
        gap: 10,
        alignItems: "center",
    },

    stepBadge: (color) => ({
        background: color,
        color: "#fff",
        borderRadius: 20,
        padding: "4px 12px",
        fontSize: 12,
        fontWeight: 700,
    }),

    instructionText: {
        fontSize: 14,
        color: "#374151",
    },


    videoWrap: {
        position: "relative",
        borderRadius: 14,
        overflow: "hidden",
        background: "#000",
        marginBottom: 20,
        border: "1px solid #e5e7eb",
    },

    video: {
        width: "100%",
        display: "block",
    },

    poseBanner: {
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(255,255,255,0.95)",
        padding: "6px 16px",
        borderRadius: 20,
        fontSize: 14,
        fontWeight: 600,
        border: "1px solid #e5e7eb",
    },

    processingOverlay: {
        position: "absolute",
        inset: 0,
        background: "rgba(255,255,255,0.85)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        fontWeight: 600,
    },

    spinner: {
        width: 34,
        height: 34,
        border: "4px solid #e5e7eb",
        borderTop: "4px solid #2563eb",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
    },


    thumbRow: {
        position: "absolute",
        bottom: 10,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: 8,
    },

    thumb: (done) => ({
        width: 48,
        height: 48,
        borderRadius: 8,
        border: `2px solid ${done ? "#22c55e" : "#e5e7eb"}`,
        objectFit: "cover",
        background: "#fff",
    }),


    progressRow: {
        display: "flex",
        justifyContent: "center",
        gap: 6,
        marginBottom: 16,
    },

    dot: (done, active) => ({
        height: 7,
        width: done || active ? 24 : 7,
        borderRadius: 4,
        background: done
            ? "#22c55e"
            : active
                ? "#2563eb"
                : "#e5e7eb",
        transition: "all .3s",
    }),

    /* Buttons */
    btn: (color, outline) => ({
        padding: "12px 26px",
        borderRadius: 8,
        border: outline ? `2px solid ${color}` : "none",
        background: outline ? "#fff" : color,
        color: outline ? color : "#fff",
        fontWeight: 700,
        fontSize: 14,
        cursor: "pointer",
    }),

    btnRow: {
        display: "flex",
        justifyContent: "center",
        gap: 12,
    },


    backdrop: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999,
    },

    modal: {
        background: "#fff",
        borderRadius: 14,
        padding: 24,
        width: "100%",
        maxWidth: 560,
        border: "1px solid #e5e7eb",
    },

    grid2: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        marginBottom: 20,
    },

    poseLabel: {
        textAlign: "center",
        fontSize: 12,
        fontWeight: 700,
        color: "#6b7280",
        marginTop: 6,
    },

    resultCard: {
        background: "#ecfdf5",
        border: "1px solid #a7f3d0",
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        textAlign: "center",
    },
};



const POSES = [
    { name: "Front", instruction: "Face the camera directly — keep your head straight" },
    { name: "Left", instruction: "Slowly turn your head to the LEFT" },
    { name: "Right", instruction: "Slowly turn your head to the RIGHT" },
    { name: "Up", instruction: "Tilt your head slightly UPWARD" },
];

const STEP_INSTRUCTIONS = {
    register_idle: "Click \"Start Registration\" — We will capture 4 face angles for accurate recognition.",
    register_capture: null,
    attendance_idle: "Position your face clearly in the frame, then click \"Mark Attendance\".",
    processing: "Please hold still while we process…",
};



const AttendanceRegister = () => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    const { Get, PostFormData, Patch } = useApi();
    const [employee, setEmployee] = useState(null);
    const [apiLoading, setApiLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [captureStep, setCaptureStep] = useState(0);
    const [capturedImages, setCapturedImages] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [mode, setMode] = useState("register");
    const [attendanceData, setAttendanceData] = useState(null);
    const [cameraError, setCameraError] = useState(null);
    const { handleToast } = useToastHandler();

    const startCamera = async () => {
        try {
            setCameraError(null);

            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error("Camera not supported");
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch (err) {
            let errorMsg = "Camera not available";

            if (err.name === "NotAllowedError") {
                errorMsg = "Camera permission denied. Please allow camera access.";
            } else if (err.name === "NotFoundError") {
                errorMsg = "No camera detected. Please connect a camera.";
            } else if (err.name === "NotReadableError") {
                errorMsg = "No camera detected. Please connect a camera.";
            }

            setCameraError(errorMsg);
        }
    };


    const stopCamera = () => streamRef.current?.getTracks().forEach((t) => t.stop());

    const fetchEnrollmentQueue = async () => {
        try {
            setApiLoading(true);
            const resp = await Get("facebioQueData");
            const pending = (resp?.results || []).filter(
                (i) => i.request_active === true && i.is_registered === false
            );
            setEmployee(pending.length > 0 ? pending[0] : null);
        } catch (err) {
            console.error("Queue API error:", err);
        } finally {
            setApiLoading(false);
        }
    };

    const captureImage = () => {
        const video = videoRef.current;
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);
        return canvas.toDataURL("image/jpeg");
    };


    const startCapture = () => { setCaptureStep(1); setCapturedImages([]); };
    const captureCurrentPose = async () => {
        const base64 = captureImage();
        const next = [...capturedImages, base64];
        setCapturedImages(next);
        if (next.length < 4) {
            await new Promise((r) => setTimeout(r, 300));
            setCaptureStep(next.length + 1);
        } else {
            setShowModal(true);
            setCaptureStep(0);
        }
    };

    const handleRecapture = () => { setCapturedImages([]); setCaptureStep(1); setShowModal(false); };
    const handleRegister = async () => {
        if (!employee || capturedImages.length !== 4) return;

        try {
            setIsProcessing(true);
            setShowModal(false);
            const formData = new FormData();
            formData.append("employee_id", employee.employee_id);
            formData.append("employee_name", employee.employee_name);
            formData.append("store", employee.store);

            for (let i = 0; i < capturedImages.length; i++) {
                const blob = await fetch(capturedImages[i]).then((r) => r.blob());

                formData.append(
                    "image",
                    blob,
                    `face_${POSES[i].name.toLowerCase()}.jpg`
                );
            }
            const response = await PostFormData("faceProfile", formData);
            const data = response?.data || response || {};

            await Patch("facebioQueData", employee.id, {
                is_registered: true,
                request_active: false,
            });
            const message =
                data.message ||
                `${employee.employee_name} registered successfully`;

            handleToast({
                type: "SUCCESS",
                message,
            });

            setCapturedImages([]);

            await fetchEnrollmentQueue();

        } catch (err) {
            console.error("Register Error:", err);

            handleToast({
                type: "ERROR",
                message: "Registration failed. Please try again.",
            });

        } finally {
            setIsProcessing(false);
        }
    };


    const handleAttendance = async () => {
        try {
            setIsProcessing(true);
            setAttendanceData(null);
            const base64 = captureImage();
            const blob = await fetch(base64).then((r) => r.blob());
            const formData = new FormData();
            formData.append("image", blob, "attendance.jpg");
            const response = await PostFormData("face-attendance", formData);
            const data = response?.data || response;
            const name = data.employee_name;
            const message = data.message;
            const timestamp = data.timestamp;

            setAttendanceData({
                name,
                message,
                timestamp,
            });
            handleToast({
                type: "SUCCESS",
                message: message,
            });
        } catch (err) {
            console.error("Attendance Error:", err);

            handleToast({
                type: "ERROR",
                message: "Attendance verification failed",
            });

        } finally {
            setIsProcessing(false);
        }
    };


    useEffect(() => {
        startCamera();
        if (mode === "register") fetchEnrollmentQueue();
        return stopCamera;
    }, [mode]);

    const getInstruction = () => {
        if (isProcessing) {
            return {
                badge: "Processing",
                color: "#ff9800",
                text: STEP_INSTRUCTIONS.processing,
                center: true,
            };
        }

        if (mode === "attendance") {
            return {
                text: STEP_INSTRUCTIONS.attendance_idle,
                center: true, 
            };
        }

        if (captureStep === 0) {
            return {
                badge: "Ready",
                color: "#607d8b",
                text: STEP_INSTRUCTIONS.register_idle,
                center: false,
            };
        }

        const p = POSES[captureStep - 1];

        return {
            badge: `Pose ${captureStep} of 4`,
            color: "#4caf50",
            text: `${p.icon}  ${p.instruction}`,
            center: false,
        };
    };

    // const getInstruction = () => {
    //     if (isProcessing) return { badge: "Processing", color: "#ff9800", text: STEP_INSTRUCTIONS.processing };
    //     if (mode === "attendance") return {  text: STEP_INSTRUCTIONS.attendance_idle ,  center: true,};
    //     if (captureStep === 0) return { badge: "Ready", color: "#607d8b", text: STEP_INSTRUCTIONS.register_idle };
    //     const p = POSES[captureStep - 1];
    //     return { badge: `Pose ${captureStep} of 4`, color: "#4caf50", text: `${p.icon}  ${p.instruction}` };
    // };
    const {
        badge,
        color: badgeColor,
        text: instructionText,
        center,
    } = getInstruction();


    return (
        <>
            <ToastContainer />
            <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
      `}</style>
            <div className="responsive-box">

                <div style={S.root}>
                    {/* <div style={{ marginBottom: 28, textAlign: "center" }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>
            Face Biometric
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#555e7a" }}>
            Register employees or mark attendance
          </p>
        </div> */}

                    <div style={S.card}>
                        {/* Tab bar */}
                        <div style={S.tabBar}>
                            <button style={S.tab(mode === "register", "#4caf50")} onClick={() => { setMode("register"); setCaptureStep(0); setCapturedImages([]); }}>
                                📋 Register
                            </button>
                            <button style={S.tab(mode === "attendance", "#2196f3")} onClick={() => { setMode("attendance"); setAttendanceData(null); }}>
                                🕐 Attendance
                            </button>
                        </div>

                        <div style={S.body}>
                            {/* Employee / status banners */}
                            {mode === "register" && apiLoading && (
                                <div style={S.banner("#1e2333", "#2d3348", "#667")}>Loading queue…</div>
                            )}
                            {mode === "register" && !apiLoading && employee && (
                                <div style={S.banner("rgba(76,175,80,0.1)", "rgba(76,175,80,0.3)", "#81c784")}>
                                    👤 &nbsp;<strong>{employee.employee_name}</strong> &nbsp;— pending registration
                                </div>
                            )}
                            {/* {mode === "register" && !apiLoading && !employee && (
                                <div style={S.banner("rgba(255,193,7,0.08)", "rgba(255,193,7,0.25)", "#f9a825",)}>
                                    &nbsp;No pending registration requests
                                </div>
                            )} */}
                            {mode === "register" &&
                                !apiLoading &&
                                !employee &&
                                cameraError !== "No camera detected. Please connect a camera." && (
                                    <div
                                        style={S.banner(
                                            "rgba(255,193,7,0.08)",
                                            "rgba(255,193,7,0.25)",
                                            "#f9a825"
                                        )}
                                    >
                                        No pending registration requests
                                    </div>
                                )}

                            {/* Attendance result */}
                            {/* {mode === "attendance" && attendanceData && (
                                <div style={S.resultCard}>
                                    <div style={{ fontSize: 28, marginBottom: 4 }}>✅</div>
                                    <div style={{ fontWeight: 800, fontSize: 17, color: "#81c784" }}>{attendanceData.name}</div>
                                    <div style={{ fontSize: 14, color: "#4caf50", marginTop: 4 }}>{attendanceData.message}</div>
                                    <div style={{ fontSize: 12, color: "#556", marginTop: 6 }}>
                                        {new Date(attendanceData.timestamp).toLocaleString()}
                                    </div>
                                </div>
                            )} */}
                            {!(mode === "register" && !employee) && cameraError !== "No camera detected. Please connect a camera." && (
                                <div
                                    style={{
                                        ...S.instructionBox,
                                        justifyContent: center ? "center" : "flex-start",
                                        textAlign: center ? "center" : "left",
                                    }}
                                >

                                    <span style={S.stepBadge(badgeColor)}>{badge}</span>
                                    <span style={S.instructionText}>{instructionText}</span>
                                </div>
                            )}

                            {mode === "register" && employee && captureStep > 0 && (
                                <div style={S.progressRow}>
                                    {POSES.map((_, i) => (
                                        <div key={i} style={S.dot(i < captureStep - 1 || capturedImages[i], i === captureStep - 1)} />
                                    ))}
                                </div>
                            )}

                            <div style={S.videoWrap}>
                                {cameraError ? (
                                    <div style={{
                                        padding: 60,
                                        textAlign: "center",
                                        background: "#f9fafb",
                                        color: "#6b7280",
                                        fontSize: 16,
                                    }}>
                                        {cameraError}
                                    </div>
                                ) : (
                                    <video ref={videoRef} autoPlay playsInline muted style={S.video} />
                                )}

                                {/* Pose overlay during registration */}
                                {mode === "register" && captureStep > 0 && (
                                    <div style={S.poseBanner}>
                                        {POSES[captureStep - 1].icon} &nbsp;{POSES[captureStep - 1].instruction}
                                    </div>
                                )}

                                {/* Thumbnails */}
                                {mode === "register" && capturedImages.length > 0 && (
                                    <div style={S.thumbRow}>
                                        {POSES.map((p, i) => (
                                            capturedImages[i]
                                                ? <img key={i} src={capturedImages[i]} alt={p.name} style={S.thumb(true)} />
                                                : <div key={i} style={{ ...S.thumb(false), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{p.icon}</div>
                                        ))}
                                    </div>
                                )}

                                {/* Processing overlay */}
                                {isProcessing && (
                                    <div style={S.processingOverlay}>
                                        <div style={S.spinner} />
                                        <span>Processing…</span>
                                    </div>
                                )}
                            </div>

                            {/* Action buttons */}
                            <div style={S.btnRow}>
                                {mode === "register" && employee && !isProcessing && (
                                    captureStep === 0
                                        ? <button style={S.btn("#4caf50")} onClick={startCapture}>▶ Start Registration</button>
                                        : <button style={S.btn("#4caf50")} onClick={captureCurrentPose}>📸 Capture Pose {captureStep}</button>
                                )}
                                {mode === "attendance" && !isProcessing && (
                                    <button style={S.btn("#2196f3")} onClick={handleAttendance}> Mark Attendance</button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>


                {showModal && (
                    <div style={S.backdrop}>
                        <div style={S.modal}>
                            <h3 style={{ margin: "0 0 18px", textAlign: "center", fontSize: 17, color: "#e8eaf0" }}>
                                Review Captures — <span style={{ color: "#81c784" }}>{employee?.employee_name}</span>
                            </h3>
                            <div style={S.grid2}>
                                {capturedImages.map((img, idx) => (
                                    <div key={idx}>
                                        <img src={img} alt={POSES[idx].name} style={{ width: "100%", borderRadius: 10, border: "2px solid #2d3348" }} />
                                        <p style={S.poseLabel}>{POSES[idx].icon} {POSES[idx].name}</p>
                                    </div>
                                ))}
                            </div>
                            <div style={S.btnRow}>
                                <button style={S.btn("#f44336", true)} onClick={handleRecapture}>↩ Recapture</button>
                                <button style={S.btn("#4caf50")} onClick={handleRegister}>Confirm & Register</button>
                            </div>
                        </div>
                    </div>

                )}
            </div>

        </>

    );
};

export default AttendanceRegister;